<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-circle</v-icon>
        My Profile
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>

        <!-- Profile Header -->
        <div class="text-center mb-6">
          <v-avatar size="80" color="#002060" class="mb-3">
            <span class="avatar-text">{{ userInitials }}</span>
          </v-avatar>
          <h2 class="profile-title mb-2">My Profile</h2>
          <p class="profile-subtitle mb-3">Update your personal information</p>
        </div>

        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <!-- Personal Information Section -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Personal Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="profile.firstName"
                label="First Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'First name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="profile.surname"
                label="Surname"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-outline"
                :rules="[v => !!v || 'Surname is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Contact Information Section -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Contact Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="profile.email"
                label="Email Address"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                disabled
                hint="Email cannot be changed"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="profile.phone"
                label="Phone Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                :rules="phoneValidationRules"
                @input="formatPhoneInput"
                placeholder="074 551 1155"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Account Information Section -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Account Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="profile.role"
                label="Role"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-tie"
                disabled
                hint="Role is managed by administrators"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="profile.department"
                label="Department"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-office-building"
                disabled
                hint="Department is managed by administrators"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                color="#002060"
                dark
                rounded
                size="large"
                :disabled="!valid || loading"
                :loading="loading"
                @click="saveProfile"
                class="mr-4"
              >
                <v-icon left>mdi-content-save</v-icon>
                {{ loading ? 'Saving...' : 'Save Changes' }}
              </v-btn>
              
              <v-btn
                color="grey"
                dark
                rounded
                size="large"
                @click="resetForm"
              >
                <v-icon left>mdi-refresh</v-icon>
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db, auth } from '../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { mapGetters } from 'vuex';
import { ActivityLogger } from '../utils/activityLogger';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';

export default {
  name: 'Profile',
  components: {
    NotificationDialog
  },
  data() {
    return {
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      profile: {
        firstName: '',
        surname: '',
        email: '',
        phone: '',
        role: '',
        department: '',
        status: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
    };
  },
  
  computed: {
    ...mapGetters(['user']),
    
    userInitials() {
      if (this.profile.firstName && this.profile.surname) {
        return (this.profile.firstName[0] + this.profile.surname[0]).toUpperCase();
      }
      return this.user?.email?.[0]?.toUpperCase() || 'U';
    },
    
    phoneValidationRules() {
      return getPhoneValidationRules();
    }
  },
  
  async created() {
    await this.loadProfile();
    this.logProfileViewed();
  },
  
  methods: {
    formatPhoneInput() {
      this.profile.phone = formatPhoneInput(this.profile.phone);
    },

    async loadProfile() {
      this.loading = true;
      try {
        const userId = this.user.uid;
        
        // Try users collection first
        let userDoc = await getDoc(doc(db, 'users', userId));
        
        if (!userDoc.exists()) {
          // If not found in users, try members collection
          userDoc = await getDoc(doc(db, 'members', userId));
        }
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          this.profile = {
            firstName: userData.firstName || '',
            surname: userData.surname || '',
            email: userData.email || this.user.email || '',
            phone: userData.phone || '',
            role: userData.role || '',
            department: userData.department || '',
            status: userData.status || 'active',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          };
        } else {
          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Profile not found.';
          this.showNotification = true;
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to load profile data.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    async saveProfile() {
      if (!this.$refs.form.validate()) return;
      
      this.loading = true;
      this.showNotification = false;
      
      try {
        const userId = this.user.uid;
        
        // Update profile data object (only editable fields)
        const profileData = {
          firstName: this.profile.firstName,
          surname: this.profile.surname,
          phone: this.profile.phone || '',
          updatedAt: new Date().toISOString()
        };
        
        // Update Firebase Auth display name
        await updateProfile(auth.currentUser, {
          displayName: `${this.profile.firstName} ${this.profile.surname}`
        });
        
        // Update Firestore document
        // Try updating in users collection first
        let userDoc = await getDoc(doc(db, 'users', userId));
        
        if (userDoc.exists()) {
          await updateDoc(doc(db, 'users', userId), profileData);
        } else {
          // If not found in users, try members collection
          userDoc = await getDoc(doc(db, 'members', userId));
          if (userDoc.exists()) {
            await updateDoc(doc(db, 'members', userId), profileData);
          } else {
            throw new Error('Profile not found in either collection');
          }
        }
        
        // Handle password change if provided
        if (this.profile.newPassword && this.profile.currentPassword) {
          await this.changePassword();
          
          // Log password change activity
          const displayName = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.passwordChanged(displayName, this.user?.uid);
        }
        
        console.log('Profile updated successfully');
        
        // Log profile update activity
        const displayName = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.profileUpdated(displayName, this.user?.uid);
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Profile updated successfully!';
        this.showNotification = true;
        
        // Clear password fields
        this.profile.currentPassword = '';
        this.profile.newPassword = '';
        this.profile.confirmPassword = '';
        
      } catch (error) {
        console.error('Error updating profile:', error);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        
        if (error.code === 'auth/wrong-password') {
          this.notificationMessage = 'Current password is incorrect.';
        } else if (error.code === 'auth/requires-recent-login') {
          this.notificationMessage = 'Please log out and log back in to change your password.';
        } else {
          this.notificationMessage = 'Failed to update profile. Please try again.';
        }
        
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },
    
    async changePassword() {
      try {
        // Re-authenticate user before changing password
        const credential = EmailAuthProvider.credential(
          this.user.email,
          this.profile.currentPassword
        );
        
        await reauthenticateWithCredential(auth.currentUser, credential);
        
        // Update password
        await updatePassword(auth.currentUser, this.profile.newPassword);
        
        console.log('Password changed successfully');
      } catch (error) {
        console.error('Error changing password:', error);
        throw error; // Re-throw to be caught by updateProfile
      }
    },

    handleNotificationClose() {
      this.showNotification = false;
    },

    logProfileViewed() {
      const currentUser = this.$store.getters.user;
      const currentUserId = currentUser?.uid;
      const currentUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
      
      ActivityLogger.profileViewed(currentUserName, currentUserId, 'Profile Page');
    },

    resetForm() {
      this.$refs.form.reset();
      this.profile.currentPassword = '';
      this.profile.newPassword = '';
      this.profile.confirmPassword = '';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.profile-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.profile-subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.avatar-text {
  font-size: 2rem;
  font-weight: 700;
  color: white;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 